import React, { useEffect, useState } from 'react';
import { Users, Loader2, RefreshCw, Search, X, CheckCircle2, Eye, FileText, Inbox } from 'lucide-react';
import { submissionsApi, SubmissionRecord, newslettersApi, NewsletterRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
/** Parse Form_Payload JSON safely */
function parsePayload(raw: string): Record<string, any> | null {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
/** Extract email from payload */
function extractEmail(payload: Record<string, any> | null): string {
  if (!payload) return '-';
  return payload.email || payload.Email || payload.EMAIL || payload.e_mail || '-';
}
/** Extract a display name from payload */
function extractName(payload: Record<string, any> | null): string {
  if (!payload) return '';
  const first = payload.firstName || payload.first_name || payload.FirstName || payload.name || '';
  const last = payload.lastName || payload.last_name || payload.LastName || '';
  return [first, last].filter(Boolean).join(' ');
}
export function Subscriptions() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [submissions, setSubmissions] = useState<SubmissionRecord[]>([]);
  const [newsletters, setNewsletters] = useState<NewsletterRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState<SubmissionRecord | null>(null);
  const fetchData = async () => {
    setLoading(true);
    try {
      const [subsData, nlData] = await Promise.all([submissionsApi.list(), newslettersApi.list()]);
      setSubmissions(subsData);
      setNewsletters(nlData);
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);
  const getNewsletterName = (newsletterId: string): string => {
    const nl = newsletters.find(n => n.Newsletter_ID === newsletterId);
    return nl?.Newsletter || newsletterId || '-';
  };
  // Filter: workspace scope → Confirmed only → search
  const confirmedSubscriptions = submissions.filter(s => {
    if (s.Team !== currentWorkspace.name) return false;
    if (currentSubRegion) {
      return !s.Region || s.Region === currentSubRegion;
    }
    return true;
  }).filter(s => s.Status === 'Confirmed');
  const filteredSubscriptions = confirmedSubscriptions.filter(s => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    const payload = parsePayload(s.Form_Payload);
    const email = extractEmail(payload).toLowerCase();
    const name = extractName(payload).toLowerCase();
    const nlName = getNewsletterName(s.Newsletter_ID).toLowerCase();
    return email.includes(q) || name.includes(q) || nlName.includes(q);
  });
  return <div className="space-y-6" data-id="element-4591">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4592">
        <div className="min-w-0" data-id="element-4593">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4594">
            Subscriptions
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4595">
            Confirmed subscribers who completed double opt-in.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-4596">
          <button onClick={fetchData} disabled={loading} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-4597">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-4598" />
          </button>
        </div>
      </div>

      {/* Search & count */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3" data-id="element-4599">
        <div className="relative flex-1 max-w-sm" data-id="element-4600">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-4601" />
          <input type="text" placeholder="Search by email, name, or newsletter..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-4602" />
          {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-4603">
              <X className="h-3.5 w-3.5" data-id="element-4604" />
            </button>}
        </div>
        <div className="flex items-center gap-1.5 text-sm text-gray-500" data-id="element-4605">
          <CheckCircle2 className="h-4 w-4 text-green-500" data-id="element-4606" />
          <span data-id="element-4607">
            {confirmedSubscriptions.length} confirmed subscriber
            {confirmedSubscriptions.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-4608">
        {loading && submissions.length === 0 ? <div className="p-12 text-center" data-id="element-4609">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-4610" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-4611">
              Loading subscriptions...
            </p>
          </div> : filteredSubscriptions.length === 0 ? <div className="p-12 text-center" data-id="element-4612">
            <Users className="mx-auto h-12 w-12 text-gray-400" data-id="element-4613" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-4614">
              {searchQuery ? 'No matching subscribers' : 'No confirmed subscribers yet'}
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-4615">
              {searchQuery ? 'Try adjusting your search.' : 'Subscribers who confirm their double opt-in email will appear here.'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-4616">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-4617">
              <thead className="bg-gray-50" data-id="element-4618">
                <tr data-id="element-4619">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4620">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4621">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4622">
                    Newsletter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4623">
                    Region
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4624">
                    Confirmed At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4625">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4626">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-4627">
                {filteredSubscriptions.map(sub => {
              const payload = parsePayload(sub.Form_Payload);
              const email = extractEmail(payload);
              const name = extractName(payload);
              return <tr key={sub.ID} className="hover:bg-gray-50" data-id="element-4628">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4629">
                        <span className="text-sm font-medium text-gray-900 truncate block max-w-[220px]" data-id="element-4630">
                          {email}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4631">
                        <span className="text-sm text-gray-600 truncate block max-w-[150px]" data-id="element-4632">
                          {name || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4633">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600 truncate max-w-[180px]" data-id="element-4634">
                          <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" data-id="element-4635" />
                          {getNewsletterName(sub.Newsletter_ID)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4636">
                        {sub.Region || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4637">
                        {sub.Confirmed_At1 ? new Date(sub.Confirmed_At1).toLocaleString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-4638">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-4639">
                          <CheckCircle2 className="h-3 w-3" data-id="element-4640" />
                          Confirmed
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-4641">
                        <button onClick={() => setSelectedSubmission(sub)} className="text-blue-600 hover:text-blue-900 text-sm inline-flex items-center gap-1" data-id="element-4642">
                          <Eye className="h-3.5 w-3.5" data-id="element-4643" />
                          View
                        </button>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Payload Modal */}
      {selectedSubmission && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4644">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-4645">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedSubmission(null)} data-id="element-4646" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg" data-id="element-4647">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-4648">
                <div data-id="element-4649">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-4650">
                    Subscriber Details
                  </h3>
                  <p className="text-xs text-gray-500 font-mono mt-0.5" data-id="element-4651">
                    ID: {selectedSubmission.ID}
                  </p>
                </div>
                <button onClick={() => setSelectedSubmission(null)} className="p-2 text-gray-400 hover:text-gray-600 rounded-md" data-id="element-4652">
                  <X className="h-5 w-5" data-id="element-4653" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4" data-id="element-4654">
                <div className="flex items-center justify-between" data-id="element-4655">
                  <span className="text-sm font-medium text-gray-700" data-id="element-4656">
                    Status
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-4657">
                    <CheckCircle2 className="h-3 w-3" data-id="element-4658" />
                    Confirmed
                  </span>
                </div>

                <div className="flex items-center justify-between" data-id="element-4659">
                  <span className="text-sm font-medium text-gray-700" data-id="element-4660">
                    Newsletter
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-4661">
                    {getNewsletterName(selectedSubmission.Newsletter_ID)}
                  </span>
                </div>

                {(() => {
              const payload = parsePayload(selectedSubmission.Form_Payload);
              if (!payload) return <div className="text-sm text-red-500" data-id="element-4662">
                        Could not parse payload
                      </div>;
              return <div className="space-y-2" data-id="element-4663">
                      <h4 className="text-sm font-medium text-gray-700" data-id="element-4664">
                        Form Data
                      </h4>
                      <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200" data-id="element-4665">
                        {Object.entries(payload).filter(([key]) => key !== 'newsletter_id' && key !== 'submitted_at').map(([key, value]) => <div key={key} className="flex items-center justify-between px-3 py-2" data-id="element-4666">
                              <span className="text-xs font-medium text-gray-500" data-id="element-4667">
                                {key}
                              </span>
                              <span className="text-sm text-gray-900 text-right max-w-[250px] truncate" data-id="element-4668">
                                {String(value)}
                              </span>
                            </div>)}
                      </div>
                      {payload.submitted_at && <div className="flex items-center justify-between pt-2" data-id="element-4669">
                          <span className="text-xs text-gray-400" data-id="element-4670">
                            Submitted at
                          </span>
                          <span className="text-xs text-gray-500" data-id="element-4671">
                            {new Date(payload.submitted_at).toLocaleString()}
                          </span>
                        </div>}
                    </div>;
            })()}

                <details className="group" data-id="element-4672">
                  <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600" data-id="element-4673">
                    View raw JSON
                  </summary>
                  <pre className="mt-2 bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto max-h-48" data-id="element-4674">
                    {JSON.stringify(parsePayload(selectedSubmission.Form_Payload), null, 2) || selectedSubmission.Form_Payload}
                  </pre>
                </details>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-4675">
                <button onClick={() => setSelectedSubmission(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4676">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}